import React from 'react';
import qs from 'qs';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslate } from 'hooks/useTranslate';
import { useSelector, useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import uk from 'dayjs/locale/uk';
import MainLayout from 'layouts/MainLayout';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Header from 'components/Header';
import Footer from 'components/Footer';
import Helmet from 'components/Helmet';
import styled from '@emotion/styled';
import RenderPageSchema from 'components/RenderPageSchema';
import Preloader from 'components/Preloader';
import { getPostInfo } from 'actions/articles';
import { getProfessionInfo } from 'actions/profession';
import Cards from 'components/Card';
import LazyLoadImage from 'components/LazyLoadImage';
import { getArticlesCategories } from 'actions/articles';
import { toggleFavorite } from 'actions/profession';
import { triggerDailyQuest } from 'actions/gamification';
import StarIcon from 'assets/icons/star.svg?react';
import StarCheckedIcon from 'assets/icons/star_checked.svg?react';
import EyeIcon from 'assets/icons/eye.svg?react';
import StarGreyIcon from 'assets/icons/fluent_copy-32-regular.svg?react';
import CopyUrlButton from 'components/CopyUrlButton';
import NeedAuthDialog from 'components/NeedAuthDialog';
import { ADSENSE_ACTIVE, DAILY, WORDS_PER_MINUTE } from 'variables/main';
import { isAuthenticated } from 'helpers/authToken';
import getCurrentId from 'helpers/getCurrentId';
import AdSense from 'components/AdSense';
import AuthorProfile from 'components/AuthorProfile';
import { generateArticleSchema, getBaseUrl, buildEntityUrl } from 'utils/seo';
const Breadcrumbs = React.lazy(() => import('components/BreadCrumbs'));

const ContentBackground = styled('section')(() => ({
  backgroundColor: 'linear-gradient(to bottom, theme.palette.grey[50], theme.palette.white.main)'
}));

const Content = styled('section')(({ theme }) => ({
  padding: '40px 100px',
  ...theme.wrapper,
  maxWidth: 960,
  minHeight: 'calc(100vh - 235px)',
  [theme.breakpoints.down('lg')]: {
    padding: 50
  },
  [theme.breakpoints.down('md')]: {
    padding: 30
  },
  [theme.breakpoints.down('sm')]: {
    padding: 20
  }
}));

const ProfessionWrapper = styled('div')(({ theme }) => ({
  marginTop: 40,
  marginBottom: 40,
  [theme.breakpoints.down('sm')]: {
    marginTop: 20,
    marginBottom: 20
  }
}));

const BreadcrumbsWrapper = styled('div')(({ theme }) => ({
  ...theme.wrapper,
  maxWidth: 1240,
  paddingTop: 60,
  boxSizing: 'border-box',
  paddingBottom: 30,
  [theme.breakpoints.down('md')]: {
    paddingTop: 20,
    paddingBottom: 10,
    paddingLeft: 20
  }
}));

const Banner = styled('div')(({ theme }) => ({
  paddingBottom: 0,
  ...theme.wrapper,
  maxWidth: 1240,
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  alignContent: 'center',
  backgroundColor: theme.palette.white.main,
  padding: 25,
  paddingLeft: 50,
  gap: 50,
  [theme.breakpoints.down('lg')]: {
    padding: 50,
    paddingBottom: 0
  },
  [theme.breakpoints.down('md')]: {
    padding: 30,
    paddingBottom: 0
  },
  [theme.breakpoints.down('sm')]: {
    padding: 20,
    paddingBottom: 0,
    flexDirection: 'column-reverse',
    marginBottom: 15
  }
}));

const Preview = styled('div')(({ theme }) => ({
  width: 505,
  height: 350,
  overflow: 'hidden',
  flex: 1,
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  },
  [theme.breakpoints.down('lg')]: {
    width: 250,
    height: 'auto'
  },
  [theme.breakpoints.down('md')]: {
    width: '100%',
    marginBottom: 30,
    borderRadius: 0
  },
  [theme.breakpoints.down('sm')]: {
    marginTop: -30,
    marginBottom: -20,
    width: 'calc(100% + 40px)'
  }
}));

const ShortInfo = styled('div')(({ theme }) => ({
  flex: 1,
  [theme.breakpoints.down('md')]: {
    paddingBottom: 20
  }
}));

const Title = styled(Typography)(({ theme }) => ({
  marginBottom: 25,
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  color: theme.palette.textPrimary.main,
  [theme.breakpoints.down('lg')]: {
    display: 'block'
  },
  [theme.breakpoints.down('md')]: {
    fontSize: 36,
    lineHeight: '41.4px',
    marginBottom: 20
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: 28,
    lineHeight: '36.4px',
    marginBottom: 20
  }
}));

const Author = styled(Typography)(({ theme }) => ({
  fontSize: 16,
  lineHeight: '20px',
  fontWeight: 600,
  marginBottom: 5,
  color: theme.palette.grey[400]
}));

const Posted = styled(Typography)(({ theme }) => ({
  color: theme.palette.textDisabled.main,
  fontSize: 16,
  lineHeight: '20px',
  marginBottom: 5
}));

const Stats = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: 20,
  '& svg': {
    marginRight: 5
  },
  '& p': {
    marginRight: 10,
    color: theme.palette.textDisabled.main,
    fontSize: 16,
    lineHeight: '20px'
  }
}));

const Category = styled(Posted)(({ theme }) => ({
  marginBottom: 20,
  fontWeight: 600,
  color: theme.palette.primary.main,
  [theme.breakpoints.down('md')]: {
    marginBottom: 10
  }
}));

const HeadLine = styled(Typography)(({ theme }) => ({
  marginBottom: 30,
  [theme.breakpoints.down('md')]: {
    fontSize: 28,
    lineHeight: '27px',
    marginBottom: 30
  }
}));

const FavoriteButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: 30,
  right: 30,
  [theme.breakpoints.down('sm')]: {
    top: 10,
    right: 15
  }
}));

const Post = () => {
  const t = useTranslate('Posts');
  const tHelmet = useTranslate('HELMET');
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [post, setPostInfo] = React.useState(null);
  const [professions, setProfessions] = React.useState([]);
  const [currentId, setCurrentId] = React.useState(getCurrentId(location));
  const postRef = React.useRef(null);
  const [readingTime, setReadingTime] = React.useState(0);
  const [categories, setCategories] = React.useState([]);
  const [isFavorite, setIsFavorite] = React.useState(false);
  const [needAuth, setNeedAuth] = React.useState(false);

  const postInfo = useSelector((state) => state.articles.state[currentId]);
  const categoriesStated = useSelector((state) => state?.articles?.categories || []);

  const handleClose = React.useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    setNeedAuth(false);
  }, []);

  React.useEffect(() => {
    const fetchData = async () => {
      const resultInfo = await dispatch(getPostInfo(currentId));
      if (!resultInfo || resultInfo instanceof Error) {
        navigate('/');
        return;
      }
      setPostInfo(resultInfo);
      setIsFavorite(resultInfo?.isFavorite);
    };

    if (!currentId) {
      navigate('/');
      return;
    }

    if (postInfo) {
      setPostInfo(postInfo);
      setIsFavorite(postInfo?.isFavorite);
      return;
    }

    fetchData();
  }, [currentId, dispatch, navigate, postInfo]);

  React.useEffect(() => {
    const newId = getCurrentId(location);
    if (newId !== currentId) {
      setCurrentId(newId);
    }
  }, [location, currentId]);

  React.useEffect(() => {
    if (!postRef?.current || !post) return;

    const wordsCount = postRef.current.innerText.split(' ').length;

    const time = Math.ceil(wordsCount / WORDS_PER_MINUTE);

    setReadingTime(t('TimeReading', { time }));
  }, [postRef, post, t]);

  React.useEffect(() => {
    if (!post) return;

    const professionsId = post.professionIds || [];

    if (!professionsId || !professionsId.length) return;

    professionsId.forEach(async (id) => {
      const query = qs.stringify({
        fields: ['_id', 'tags', 'photoLinks', 'name', 'descriptionShort']
      });
      const profession = await dispatch(getProfessionInfo(id, `?${query}`));
      // Only add valid profession objects (must have _id and name)
      if (!profession || !profession._id || !profession.name) return;
      setProfessions((prev) => [...prev, profession]);
    });
  }, [post, dispatch]);

  React.useEffect(() => {
    const fetchCategories = async () => {
      const result = await dispatch(getArticlesCategories());
      setCategories(result);
    };

    if (categoriesStated.length) {
      setCategories(categoriesStated);
      return;
    }

    fetchCategories();
  }, [dispatch, categoriesStated]);

  React.useEffect(() => {
    const listenScroll = () => {
      if (!postRef?.current) return;
      const isBottom = postRef.current.getBoundingClientRect().bottom <= window.innerHeight;
      if (!isBottom) return;
      dispatch(triggerDailyQuest(DAILY.readArticle));
      window.removeEventListener('scroll', listenScroll);
    };

    window.addEventListener('scroll', listenScroll);

    return () => window.removeEventListener('scroll', listenScroll);
  }, [dispatch, postRef]);

  const handleToggleFavorite = React.useCallback(() => {
    if (!isAuthenticated()) {
      setNeedAuth(true);
      return;
    }

    dispatch(toggleFavorite(currentId, isFavorite, 'articles'));
    setIsFavorite(!isFavorite);
  }, [dispatch, currentId, isFavorite]);

  const categoryName = React.useMemo(() => {
    if (!post?.category) return null;

    const category = categories.find(({ _id }) => _id === post?.category?._id);

    if (!category) return null;

    return category?.name;
  }, [post, categories]);

  const publishDate = React.useMemo(
    () => dayjs(post?.publishedAt).locale(uk).format('DD MMMM YYYY'),
    [post]
  );

  const breadcrumbs = React.useMemo(() => {
    return [
      {
        label: 'Home',
        link: '/'
      },
      {
        label: 'Posts',
        link: '/articles'
      },
      {
        label: post?.title,
        raw: true
      }
    ];
  }, [post]);

  // Generate structured data for the article
  const baseUrl = React.useMemo(() => getBaseUrl(), []);

  const articleSchema = React.useMemo(() => {
    if (!post) return null;
    return generateArticleSchema(post, baseUrl, post?.authorName);
  }, [post, baseUrl]);

  const canonicalUrl = React.useMemo(() => {
    if (!post) return undefined;
    return buildEntityUrl('article', post.slug, post._id);
  }, [post]);

  if (!post) {
    return <Preloader />;
  }

  return (
    <>
      <Helmet
        page="article"
        meta={{
          title: post?.meta?.title || post?.title || tHelmet('postOgTitle'),
          description: post?.meta?.description || post?.description || tHelmet('postDescription'),
          ogTitle: post?.meta?.ogTitle || post?.meta?.title || post?.title || tHelmet('postOgTitle'),
          ogDescription: post?.meta?.ogDescription || post?.meta?.description || post?.description || tHelmet('postOgDescription')
        }}
        metaImageUrl={post?.postImage ? `${post?.postImage}/w1200h627` : null}
        schema={articleSchema}
        breadcrumbs={breadcrumbs}
        article={post}
        publishedTime={post?.publishedAt}
        modifiedTime={post?.updatedAt}
        author={post?.authorName}
        section={categoryName}
        canonicalUrl={canonicalUrl}
        ogType="article"
        includeHreflang={true}
      />
      <MainLayout grey={true}>
        <Header />
        <BreadcrumbsWrapper>
          <Breadcrumbs breadcrumbs={breadcrumbs} />
        </BreadcrumbsWrapper>

        {ADSENSE_ACTIVE && (
          <AdSense
            adSlot="6943582871"
            adLayout="in-article"
            style={{ display: 'block', textAlign: 'center' }}
          />
        )}

        <ContentBackground>
          <Banner style={{ position: 'relative' }}>
            <ShortInfo>
              {post?.category && <Category>{categoryName}</Category>}
              <Title variant="h1">{post?.title}</Title>
              {post.author ? (
                <Author>
                  {t('Author')}
                  {': '}
                  {post.author.nameUA || post.author.nameEN}
                </Author>
              ) : post.authorName || post.authorNameUA ? (
                <Author>
                  {t('Author')}
                  {': '}
                  {post.authorName || post.authorNameUA}
                </Author>
              ) : null}
              <Posted>
                {post?.publishedAt && (
                  <>
                    {t('Published')}
                    {': '}
                    {publishDate}
                    {', '}
                  </>
                )}
                {readingTime}
              </Posted>

              {post?.viewsCount || post?.favoritesCount ? (
                <Stats>
                  {post?.viewsCount ? (
                    <Stats>
                      <EyeIcon />
                      <Typography>{post?.viewsCount}</Typography>
                    </Stats>
                  ) : null}

                  {post?.favoritesCount ? (
                    <Stats>
                      <StarGreyIcon />
                      <Typography>{post?.favoritesCount}</Typography>
                    </Stats>
                  ) : null}
                </Stats>
              ) : null}

              <CopyUrlButton authRequired={false} />
            </ShortInfo>
            {post?.postImage && (
              <Preview>
                <LazyLoadImage
                  alt={post?.title}
                  width={'100%'}
                  src={`${post?.postImage}/w505h350`}
                />
              </Preview>
            )}
            <FavoriteButton onClick={handleToggleFavorite} aria-label="favorite" size="small">
              {isFavorite ? <StarCheckedIcon /> : <StarIcon />}
            </FavoriteButton>
          </Banner>
          <Content ref={postRef}>
            <RenderPageSchema schema={post} hideTitle={true} />
            {post?.author && <AuthorProfile author={post.author} />}
            {professions.length > 0 ? (
              <ProfessionWrapper>
                <HeadLine variant="h2">{t('PostProfessions')}</HeadLine>
                <Cards list={professions} columns={2} />
              </ProfessionWrapper>
            ) : null}
          </Content>
        </ContentBackground>
        <Footer />
        <NeedAuthDialog handleClose={handleClose} needAuth={needAuth} />
      </MainLayout>
    </>
  );
};

export default Post;
